import React from 'react';
import { Route, Switch, withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Cookies from 'js-cookie';
import moment from 'moment';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import LoadingSpinner from '~/components/LoadingSpinner';
import {
  ORGANIZATION_CELEBRATION_SETTINGS,
  ORGANIZATION_CELEBRATION_TYPES,
  REIHUB_FREEMIUM_COOKIE_KEY,
  REIHUB_FREEMIUM_RELEASE_DATE,
  REIHUB_FREEMIUM_VARIANTS,
} from '~/constants';
import getRootDomain from '~/helpers/utils/getRootDomain';
import Onboarding from '~/layouts/Onboarding/Container';
import queryClient from '~/queryClient';

import client from '../feathers';
import { create, find, get, patch } from '../feathersWrapper';
import { asyncSetState } from '../functions/InputHandlers';
import history from '../history';
import Accountant from '../layouts/Accountant';
import Admin from '../layouts/Admin';
import Dashboard from '../layouts/Dashboard';
import Authenticate from '../routes/AuthenticateRoutes';

import { PersonContext } from './PersonContext';

const STATE_IS_TOUR_VISIBLE_KEY = 'isTourVisible';

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.grey['100'],
    minHeight: '100vh',
    minWidth: '100%',
  },
});

class PersonProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState = () => {
    const isTourVisible = localStorage.getItem(STATE_IS_TOUR_VISIBLE_KEY);
    if (isTourVisible === null) {
      localStorage.setItem(STATE_IS_TOUR_VISIBLE_KEY, false);
    }

    return {
      userId: null,
      organizationId: null,
      loading: true,
      adminLogin: false,
      accountantLogin: false,
      accountingFirmId: null,
      showWelcome: false,
      promptPayment: false,
      dismissPayment: true,
      promptUpdatePaymentMethod: false,
      unpaidInvoiceData: null,
      autoSelectOrganization: true,
      sessionSource: null,
      demo: false,
      isProduction: window.location.hostname === 'app.reihub.net',
      isTourVisible: localStorage.getItem(STATE_IS_TOUR_VISIBLE_KEY) === 'true',
      isScheduleCallDialogVisible: false,
      redirectPath: null,
      settings: {},
      selectedOrganizationCelebrationType: null,
      isOrganizationCelebrationVisible: false,
    };
  };

  async componentDidMount() {
    this.checkCache();

    const { location } = this.props;

    if (location.pathname === '/invite') {
      await this.logOut();
      return;
    }

    const queryParams = queryString.parse(location.search);

    // Try to authenticate with any JWT stored in localStorage
    client
      .reAuthenticate()
      .then(async (response) => {
        this.setReiHubCoreValueLocally(response.user.createdAt);

        // defaults the subscription dialog
        if (queryParams.subscribe) {
          localStorage.setItem('subscribe', queryParams.subscribe);
          delete queryParams.subscribe;
          history.replace({
            search: queryString.stringify(queryParams),
          });
        }
        await this.checkLocalStorage();
        this.setPerson(response).then(() => {
          if (queryParams.page && window.location.pathname !== '/sso') {
            localStorage.setItem('page', queryParams.page);
            delete queryParams.page;
            history.replace({
              search: queryString.stringify(queryParams),
            });
          }
          this.setState({ sessionSource: 'token', loading: false });
        });
      })
      .catch(() => {
        const signupExperiment = localStorage.getItem(REIHUB_FREEMIUM_COOKIE_KEY);

        localStorage.clear();

        if (signupExperiment) {
          localStorage.setItem(REIHUB_FREEMIUM_COOKIE_KEY, signupExperiment);
        }

        if (queryParams.page) {
          localStorage.setItem('page', queryParams.page);
          delete queryParams.page;
          history.replace({
            search: queryString.stringify(queryParams),
          });
        }
        // defaults the subscription dialog
        if (queryParams.subscribe) {
          localStorage.setItem('subscribe', queryParams.subscribe);
          delete queryParams.subscribe;
          history.replace({
            search: queryString.stringify(queryParams),
          });
        }
        this.setState({ loading: false });
      });
  }

  checkLocalStorage = async () => {
    // for admin
    if (parseInt(localStorage.getItem('adminLogin'), 10) === 1) {
      this.setAdmin();
    }

    // for accountant
    if (parseInt(localStorage.getItem('accountantLogin'), 10) === 1) {
      this.setState({ accountantLogin: true });
    }
    if (localStorage.getItem('accountingFirmId')) {
      this.setState({ accountingFirmId: parseInt(localStorage.getItem('accountingFirmId'), 10) });
    }

    // for dashboard
    if (localStorage.getItem('organizationId')) {
      await this.setOrganizationId(parseInt(localStorage.getItem('organizationId'), 10));
    }

    if (localStorage.getItem('adminUserSelect')) {
      this.setAdminUserSelect(JSON.parse(localStorage.getItem('adminUserSelect')));
    }
  };

  checkCache = async () => {
    if (window.location.hostname === 'localhost') {
      return;
    }
    await fetch(`/meta.json?time=${Date.now()}`)
      .then((response) => response.json())
      .then((meta) => {
        const latestVersion = meta.version;
        const currentVersion = global.appVersion;
        if (latestVersion !== currentVersion) {
          window.location.reload(true);
        }
      });
  };

  setPerson = async (authenticateResponse) => {
    const { location } = this.props;
    const { adminLogin } = this.state;
    const parsed = queryString.parse(location.search);

    const { firstName, lastName, email, phone, systemPassword, isAccountant } = authenticateResponse.user;
    window.pagesense = window.pagesense || [];
    window.pagesense.push(['identifyUser', email]);
    asyncSetState(
      {
        user: authenticateResponse.user,
        firstName,
        lastName,
        email,
        phone,
        systemPassword,
        isAccountant,
        userId: authenticateResponse.user.id,
      },
      this,
    );

    // fire identify in atatus
    if (window.atatus) {
      window.atatus.setUser(
        authenticateResponse.user.id,
        authenticateResponse.user.email,
        `${authenticateResponse.user.firstName} ${authenticateResponse.user.lastName}`,
      );
    }

    // fire identify in segment
    if (window.analytics && !adminLogin) {
      const traits = {
        createdAt: new Date(authenticateResponse.user.createdAt).toDateString(),
        partnerName: authenticateResponse.user.partnerName,
      };
      if (!authenticateResponse.user.partnerName || authenticateResponse.user.partnerName === 'TurboTenant') {
        // only fill PII for direct users or TurboTenant partner users
        traits.firstName = authenticateResponse.user.firstName;
        traits.lastName = authenticateResponse.user.lastName;
        traits.email = email;
        traits.phone = authenticateResponse.user.phone;
        traits.partnerUserId = authenticateResponse.user.partnerUserId;
      }

      window.analytics.ready(() => {
        window.analytics.identify(
          authenticateResponse.user.id,
          { ...traits },
          {} /* options */,
          () => {} /* callback */,
        );
      });
    }

    if (parsed.organizationId) {
      // localStorage.setItem('organizationId', parsed.organizationId);
      this.setOrganizationId(parseInt(parsed.organizationId, 10));
    }

    window.addEventListener('focus', this.checkLogin);

    return true;
  };

  setAdminUserSelect = async (value) => {
    asyncSetState(
      {
        adminUserSelect: value,
        autoSelectOrganization: false,
      },
      this,
    );
    localStorage.setItem('adminUserSelect', JSON.stringify(value));
  };

  checkLogin = async () => {
    const { demo } = this.state;
    await client.reAuthenticate().catch(async () => {
      if (demo) {
        return;
      }
      await this.logOut();
    });
  };

  setContextState = (data) => {
    this.setState(data);
  };

  setAdmin = () => {
    this.setState({ adminLogin: true });
  };

  logOut = async () => {
    await client.logout().then(() => {
      this.setState(this.getInitialState());
      const signupExperiment = localStorage.getItem(REIHUB_FREEMIUM_COOKIE_KEY);

      localStorage.clear();

      if (signupExperiment) {
        localStorage.setItem(REIHUB_FREEMIUM_COOKIE_KEY, signupExperiment);
      }

      queryClient.invalidateQueries();

      this.setState({ loading: false });
      window.removeEventListener('focus', this.checkLogin);
      Cookies.remove('ajs_user_id');
    });
  };

  setDismissPayment = (dismissPayment) => {
    this.setState({ dismissPayment });
  };

  setIsTourVisible = (isTourVisible) => {
    localStorage.setItem(STATE_IS_TOUR_VISIBLE_KEY, isTourVisible);
    this.setState({ isTourVisible });
  };

  setIsScheduleCallDialogVisible = (isScheduleCallDialogVisible) => {
    this.setState({ isScheduleCallDialogVisible });
  };

  setDismissUpdateFailedPaymentMethod = (dismissUpdateFailedPaymentMethod) => {
    this.setState({ dismissUpdateFailedPaymentMethod });
  };

  setOrganizationSettings = async (settings) => {
    await patch(this, 'organizations', this.state.organizationId, { settings });
    this.setState({ settings });
  };

  viewDemoPortfolio = async () => {
    const demoId = parseInt(import.meta.env.VITE_DEMO_ORGANIZATION_ID, 10);
    const turboTenantDemoId = parseInt(import.meta.env.VITE_TURBOTENANT_DEMO_ORGANIZATION_ID, 10);
    const rentRediDemoId = parseInt(import.meta.env.VITE_RENTREDI_DEMO_ORGANIZATION_ID, 10);

    const { partnerName, user } = this.state;
    const { location } = this.props;
    if (
      (location.pathname === '/turbotenant' || partnerName === 'TurboTenant' || user?.partnerName === 'TurboTenant') &&
      turboTenantDemoId
    ) {
      await this.setOrganizationId(turboTenantDemoId);
    } else if (
      (location.pathname === '/rentredi' || partnerName === 'RentRedi' || user?.partnerName === 'RentRedi') &&
      rentRediDemoId
    ) {
      await this.setOrganizationId(rentRediDemoId);
    } else {
      await this.setOrganizationId(demoId);
    }
    history.push('/reload?link=/dashboard');
  };

  setOrganizationId = async (organizationId) => {
    if (organizationId === null) {
      this.setState({ organizationId: null, demo: false });
      localStorage.removeItem('organizationId');
      localStorage.removeItem('adminUserSelect');
      return;
    }
    const { adminLogin } = this.state;
    let promptPayment = false;
    let dismissPayment = true;
    let promptUpdatePaymentMethod = false;
    const dismissUpdateFailedPaymentMethod = false;
    let unpaidInvoiceData = {
      total: null,
      dueDate: null,
    };
    const organization = await get(this, 'organizations', organizationId);

    if (!organization.stripeSubscription && !organization.partnerSubscribed && !organization.demo) {
      promptPayment = true;
      if (!adminLogin && moment().isAfter(organization.freeTrialExpires, 'day')) {
        dismissPayment = false;
      }

      if (localStorage.getItem('subscribe') && !organization.showWelcome) {
        dismissPayment = false;
        localStorage.removeItem('subscribe');
      }
    }

    if (organization.stripeSubscription && ['past_due', 'unpaid'].includes(organization.stripeSubscriptionStatus)) {
      promptUpdatePaymentMethod = true;
      unpaidInvoiceData = organization.unpaidInvoiceData;
    }

    let airbnbAccountActive = false;
    let airbnbAccountId = null;
    let vrboAccountActive = false;
    let vrboAccountId = null;
    const account = await find(this, 'accounts', {
      query: {
        organizationId,
        $limit: 2,
        default: ['airbnb', 'vrbo'],
      },
    });
    account.data.forEach((result) => {
      if (result.default === 'airbnb') {
        airbnbAccountId = result.id;
        if (!result.inactive) {
          airbnbAccountActive = true;
        }
      }
      if (result.default === 'vrbo') {
        vrboAccountId = result.id;
        if (!result.inactive) {
          vrboAccountActive = true;
        }
      }
    });

    localStorage.setItem('organizationId', organizationId);
    await asyncSetState(
      {
        organizationId,
        multiEntity: organization.multiEntity === true,
        vendorTracking: organization.vendorTracking === true,
        recurringTransactions: organization.recurringTransactions === true,
        basis: organization.basis,
        organizationName: organization.name,
        partnerSubscribed: organization.partnerSubscribed,
        partnerName: organization.partnerName,
        promptPayment,
        dismissPayment,
        promptUpdatePaymentMethod,
        dismissUpdateFailedPaymentMethod,
        unpaidInvoiceData,
        showWelcome: organization.showWelcome === true,
        airbnbAccountActive,
        airbnbAccountId,
        vrboAccountActive,
        vrboAccountId,
        freeTrialExpires: organization.freeTrialExpires,
        organizationCreated: organization.createdAt,
        bookkeepingStartDate: organization.bookkeepingStartDate,
        bookkeepingCloseDate: organization.bookkeepingCloseDate,
        demo: organization.demo,
        subscriptionDate: organization.subscriptionDate,
        subscriptionPaymentMethod: organization.subscriptionPaymentMethod,
        subscriptionItem: organization.subscriptionItem,
        expertSupportSubscription: organization.expertSupportSubscription,
        freemiumSubscription: organization.freemiumSubscription,
        subscriptionMaxUnits: organization.subscriptionMaxUnits,
        settings: organization.settings,
      },
      this,
    );

    // This fires a call which syncs yodlee accounts on login or selecting an organization
    // It does not await the return so will execute in the background
    if (!organization.demo) {
      create(this, 'yodlee-accounts', { organizationId }, true).catch((e) => {
        console.error(`Error while syncing yodlee accouts: ${e.message}`);
      });
      create(this, 'plaid-item-status', { organizationId }, true).catch((e) => {
        console.error(`Error while syncing plaid accouts: ${e.message}`);
      });
    }
  };

  setReiHubCoreValueLocally = (userCreatedAt) => {
    // If users already registered before the freemium release date, we need to set the value to EXISTING
    if (moment(userCreatedAt).isAfter(REIHUB_FREEMIUM_RELEASE_DATE)) {
      return;
    }

    // Remove the cookie if it exists
    Cookies.remove(REIHUB_FREEMIUM_COOKIE_KEY);

    Cookies.set(REIHUB_FREEMIUM_COOKIE_KEY, REIHUB_FREEMIUM_VARIANTS.EXISTING, {
      domain: `.${getRootDomain(window.location.hostname)}`,
      path: '/',
    });
    localStorage.setItem(REIHUB_FREEMIUM_COOKIE_KEY, REIHUB_FREEMIUM_VARIANTS.EXISTING);
  };

  getComponent = () => {
    const { userId, organizationId, adminLogin, accountingFirmId, demo, adminUserSelect, showWelcome } = this.state;
    if (userId !== null && organizationId !== null) {
      if (showWelcome) {
        return Onboarding;
      }
      return Dashboard;
    }
    if (demo) {
      return Dashboard;
    }
    if (userId !== null && accountingFirmId !== null) {
      return Accountant;
    }
    if (userId !== null && adminLogin && !adminUserSelect) {
      return Admin;
    }
    return Authenticate;
  };

  showOrganizationCelebration = (selectedOrganizationCelebrationType) => {
    this.setState({ isOrganizationCelebrationVisible: true, selectedOrganizationCelebrationType });
  };

  hideOrganizationCelebration = () => {
    this.setState({ isOrganizationCelebrationVisible: false });
  };

  updateOrganizationCelebrations = async () => {
    const allCelebrationsShown = Object.values(ORGANIZATION_CELEBRATION_SETTINGS).every(
      (celebrationKey) => this.state.settings[celebrationKey],
    );
    if (allCelebrationsShown) return;

    const [organization, journals] = await Promise.all([
      get(this, 'organizations', this.state.organizationId),
      find(this, 'journals', {
        query: { organizationId: this.state.organizationId, partnerPaymentId: null, $limit: 0 },
      }),
    ]);

    const settingsFromServer = organization.settings;
    let selectedOrganizationCelebrationType = null;

    const updateCelebration = (celebrationType) => {
      selectedOrganizationCelebrationType = celebrationType;
    };

    if (journals?.total && journals.total <= 150) {
      if (journals.total < 25 && !settingsFromServer[ORGANIZATION_CELEBRATION_SETTINGS.FIRST]) {
        updateCelebration(ORGANIZATION_CELEBRATION_TYPES.FIRST);
      }

      if (
        journals.total >= 25 &&
        journals.total < 50 &&
        !settingsFromServer[ORGANIZATION_CELEBRATION_SETTINGS.MORE_THAN_25]
      ) {
        updateCelebration(ORGANIZATION_CELEBRATION_TYPES.MORE_THAN_25);
      }

      if (
        journals.total >= 50 &&
        journals.total < 100 &&
        !settingsFromServer[ORGANIZATION_CELEBRATION_SETTINGS.MORE_THAN_50]
      ) {
        updateCelebration(ORGANIZATION_CELEBRATION_TYPES.MORE_THAN_50);
      }

      if (
        journals.total >= 100 &&
        journals.total <= 150 &&
        !settingsFromServer[ORGANIZATION_CELEBRATION_SETTINGS.MORE_THAN_100]
      ) {
        updateCelebration(ORGANIZATION_CELEBRATION_TYPES.MORE_THAN_100);
      }

      if (selectedOrganizationCelebrationType) {
        this.showOrganizationCelebration(selectedOrganizationCelebrationType);
        this.setOrganizationSettings({
          ...settingsFromServer,
          [ORGANIZATION_CELEBRATION_SETTINGS[selectedOrganizationCelebrationType]]: true,
        });
      }
    }
  };

  render() {
    const { loading } = this.state;
    const { classes, triggerSnackbar } = this.props;

    return (
      <PersonContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          ...this.state,
          setPerson: this.setPerson,
          setContextState: this.setContextState,
          checkLogin: this.checkLogin,
          logOut: this.logOut,
          setOrganizationId: this.setOrganizationId,
          setDismissPayment: this.setDismissPayment,
          setDismissUpdateFailedPaymentMethod: this.setDismissUpdateFailedPaymentMethod,
          setAdmin: this.setAdmin,
          checkCache: this.checkCache,
          viewDemoPortfolio: this.viewDemoPortfolio,
          setIsTourVisible: this.setIsTourVisible,
          setIsScheduleCallDialogVisible: this.setIsScheduleCallDialogVisible,
          setAdminUserSelect: this.setAdminUserSelect,
          setReiHubCoreValueLocally: this.setReiHubCoreValueLocally,
          setOrganizationSettings: this.setOrganizationSettings,
          triggerSnackbar,
          updateOrganizationCelebrations: this.updateOrganizationCelebrations,
          hideOrganizationCelebration: this.hideOrganizationCelebration,
        }}
      >
        <div className={classes.root}>
          {loading ? (
            <LoadingSpinner size="medium" hasHeader />
          ) : (
            <Switch>
              <Route path="/" component={this.getComponent()} />
            </Switch>
          )}
        </div>
      </PersonContext.Provider>
    );
  }
}

PersonProvider.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  location: PropTypes.objectOf(PropTypes.any).isRequired,
  triggerSnackbar: PropTypes.func.isRequired,
};

export default withRouter(withStyles(styles)(PersonProvider));
